/**
 * @see https://umijs.org/zh-CN/plugins/plugin-access
 * */
export default function access(
  initialState:
    | { currentUser?: USER_API.User; userSettings?: USER_API.UserSettingsType }
    | undefined,
) {
  const { currentUser, userSettings } = initialState ?? {};

  const isFeeManaged = userSettings?.isFeeManaged === true;

  const checkPermission = (user: USER_API.User | undefined, permission: string) => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;

    const revokedPermissions = user.revokedPermissions || [];
    if (revokedPermissions.includes(permission)) {
      return false;
    }

    return user.role === 'USER' || user.role === 'SUB_USER';
  };

  return {
    canAdmin: currentUser?.role === 'ADMIN',
    canUser: currentUser?.role === 'USER',
    canSubUser: currentUser?.role === 'SUB_USER',
    canUserAndSubUser:
      !!currentUser && (currentUser.role === 'USER' || currentUser.role === 'SUB_USER'),
    // Specific permissions based on the revoked list
    canReadTransaction: checkPermission(currentUser, 'ReadTransaction'),
    canCreateTransaction: checkPermission(currentUser, 'CreateTransaction'),
    canUpdateTransaction: checkPermission(currentUser, 'UpdateTransaction'),
    canDeleteTransaction: checkPermission(currentUser, 'DeleteTransaction'),
    canExportTransaction: checkPermission(currentUser, 'ExportTransaction'),
    canReadDashboard: checkPermission(currentUser, 'ReadDashboard'),
    canReadWallet: checkPermission(currentUser, 'ReadWallet'),

    // Hot Wallet
    canCreateConsolidation: checkPermission(currentUser, 'CreateConsolidation'),
    canReadConsolidation: checkPermission(currentUser, 'ReadConsolidation'),
    canReadHotWallet: checkPermission(currentUser, 'ReadHotWallet'),
    canReadHotWalletWithdrawals: checkPermission(currentUser, 'ReadHotWalletWithdrawals'),
    canInitiateHotWalletWithdrawals: checkPermission(currentUser, 'InitiateHotWalletWithdrawals'),
    canFreezeOrUnfreeze: checkPermission(currentUser, 'WriteHotWalletFreezeOrUnFreeze'),
    canResendWithdrawWebhook: checkPermission(currentUser, 'ResendWithdrawWebhook'),
    canResendTransactionWebhook: checkPermission(currentUser, 'ResendTransactionWebhook'),

    // Withdraw Requests
    canReadWithdrawRequests: checkPermission(currentUser, 'ReadWithdrawRequest'),
    canWriteWithdrawRequests: checkPermission(currentUser, 'WriteWithdrawRequest'),

    // Fees
    canCreateFeeConsolidation: checkPermission(currentUser, 'CreateConsolidationFee'),
    canReadFeeConsolidation: checkPermission(currentUser, 'ReadConsolidationFee') && !isFeeManaged,
    canReadFeeHotWallet: checkPermission(currentUser, 'ReadFeeHotWallet') && !isFeeManaged,
    canReadFeeHotWalletWithdrawals:
      checkPermission(currentUser, 'ReadFeeHotWalletWithdrawals') && !isFeeManaged,
    canInitiateFeeHotWalletWithdrawals: checkPermission(
      currentUser,
      'InitiateFeeHotWalletWithdrawals',
    ),

    // Dashboard
    canReadFeeBalance: checkPermission(currentUser, 'ReadFeeBalance') && !isFeeManaged,
  };
}
